import React from 'react';
import { BookingPositionHotel, BookingPositionState } from '../model/BookingPositionState';
import { monthLabel, nightsWord, positionTier, rankValue, sameCityHotels, whole } from '../BookingRanking.utilities';
import { HotelNameCell, RankingCard, RankingCardHead, TH } from '../BookingRankingParts';

type Tile = { typical: number | null, typicalAfter500: boolean, withPosition: number, soldOut: number };

// Feature-local Screen Part (user decision of 2026-10-05): the month tile on the colour of the position tier.
const MonthTile: React.FunctionComponent<{ tile: Tile }> = ({ tile }) => {
	const base = 'flex h-11 flex-col items-center justify-center rounded-md px-1 text-center';
	if (tile.typical === null && !tile.typicalAfter500) {
		return tile.soldOut > 0
			? <div className={`${base} bg-gray-100 text-xs font-semibold text-gray-700`}>Sold out</div>
			// no position and no sold-out night = no data (3 Apr 2027: RoomPulse has no price check of the night)
			: <div className={`${base} border border-dashed border-gray-500 text-xs font-semibold text-gray-600`}>No data</div>;
	}
	const tier = positionTier(rankValue(tile.typical, tile.typicalAfter500));
	return <div className={`${base} ${tier?.className ?? ''}`}>
		<span className={tile.typicalAfter500 ? 'whitespace-nowrap text-xs font-semibold' : 'text-sm font-bold'}>
			{tile.typicalAfter500 ? 'Not in 500' : whole(tile.typical as number)}
		</span>
		{tile.withPosition < 4 && <span className="whitespace-nowrap text-xs leading-4">{nightsWord(tile.withPosition)}</span>}
	</div>;
};

const allTile = (hotel: BookingPositionHotel): Tile => ({
	typical: hotel.typical,
	typicalAfter500: hotel.typicalAfter500,
	withPosition: hotel.nightCounts.withPosition,
	soldOut: hotel.nightCounts.soldOut,
});

// Month by month: the Typical position per stay month and for all 6 months.
const MonthTable: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) =>
	<RankingCard>
		<RankingCardHead title="Month by month"
			subtitle="Typical position per stay month · a small line shows a month with fewer than 4 nights with a position" />
		<div className="overflow-x-auto">
			<table className="min-w-full">
				<thead><tr className="border-b border-gray-200">
					<th className={`${TH} sticky left-0 z-10 bg-white pl-4 sm:pl-5`}>Hotel</th>
					{state.months.map((m) => <th key={m.month} className={`${TH} text-center`}>
						{monthLabel(m.month)}<span className="block font-normal text-gray-500">{nightsWord(m.nights)}</span>
					</th>)}
					<th className={`${TH} border-l border-gray-200 pr-4 text-center sm:pr-5`}>
						All 6 months<span className="block font-normal text-gray-500">{nightsWord(state.nights.length)}</span>
					</th>
				</tr></thead>
				<tbody className="divide-y divide-gray-100">
					{sameCityHotels(state).map((hotel) => <tr key={hotel.hotelId} className={hotel.isMain ? 'bg-sky-50' : ''}>
						<td className={`sticky left-0 z-10 py-1.5 pl-4 pr-3 sm:pl-5 ${hotel.isMain ? 'bg-sky-50' : 'bg-white'}`}>
							<span className="block w-28 sm:w-64"><HotelNameCell hotel={hotel} /></span>
						</td>
						{hotel.months.map((m) => <td key={m.month} className="px-1.5 py-1.5"><span className="block w-20"><MonthTile tile={m} /></span></td>)}
						<td className="border-l border-gray-200 py-1.5 pl-1.5 pr-4 sm:pr-5"><span className="block w-20"><MonthTile tile={allTile(hotel)} /></span></td>
					</tr>)}
				</tbody>
			</table>
		</div>
	</RankingCard>;

export default MonthTable;
